// PR-52 — 5★ anlatım yüzeyi (Seçenek D). MyLASA'nın ana kalite sinyali olan
// yıldız sisteminin anlamını kullanıcıya kısa/premium anlatır.
//
// KİLİTLİ ÜRÜN KURALI: gizli formül / threshold / ranking gücü / Gold garantisi
// / MyCards drop ihtimali / para-değer SIZDIRILMAZ. Yalnız kültürü anlatır:
// yıldız = bilinçli kalite sinyali; kalp/beğeni YOKTUR.
//
// Kullanım deseni ToastBoot ile aynı: global `openStarInfo()` çağrısı lazy bir
// React root mount eder → herhangi bir rating yüzeyi (StarRatingV2 popover,
// RateStarsV3) App ağacına dokunmadan sheet'i açabilir.
import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import useFocusTrap from "../hooks/useFocusTrap";
// IG paritesi / kanon URL kuralı — anlatım yüzeyi kendi adresini alır.
import useBilgiSayfasiUrl from "../hooks/useBilgiSayfasiUrl";
import "./StarInfoSheet.css";

const POINTS: { icon: string; title: string; desc: string }[] = [
  {
    icon: "✦",
    title: "1–5 arası puanla",
    desc: "Bir içeriğe 1 ile 5 yıldız arasında puan verebilirsin. Bu puanlar topluluk deneyimini daha güvenilir hâle getirmek için kullanılır.",
  },
  {
    icon: "★",
    title: "Kalp değil, yıldız",
    desc: "MyLASA'da kalp/beğeni yoktur; yıldız vardır. Puanını bilinçli seçmen önemlidir — yıldız bir kalite değerlendirmesidir.",
  },
  {
    icon: "⚖️",
    title: "Ödül veya garanti değil",
    desc: "Yıldızlar otomatik ödül, para, sıralama garantisi veya gerçek dünya avantajı anlamına gelmez.",
  },
];

function StarInfoSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [backdrop, setBackdrop] = useState<HTMLDivElement | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  // EMİR #98 Dalga-2 (b) — beyan kancadan geliyor (hep-mounted sheet sınıfı:
  // yalnız CSS `open` sınıfıyla kayıyordu, `aria-modal` KOŞULSUZ yazılıydı →
  // kapalıyken de «arka planı yok say» diyordu). Kanca Escape + Tab sarma +
  // odak iadesini de getirir → yerel Escape effect'i kaldırıldı (çakışırdı).
  //
  // ⚠️ BU İKİSİNİ ÖLÇÜM DEĞİL TARAMA BULDU: dinlenme-hâli probu yalnız
  // UĞRADIĞI rotalarda görebiliyor; bu sheet'ler o rotalarda mount değildi.
  // Ölçüm gördüğünü kanıtlar, görmediğini YOKSAYMAZ — kaynak taraması
  // ölçümün kapsamadığı yeri kapattı.
  const katman = useFocusTrap({ open, onClose, containerRef: sheetRef });
  // `/info/star` — açılışta pushState, kapanışta geri; soğuk yük host
  // (`infoSheetHost`) tarafından açılır. Kural sheet'in kendisinde yaşar
  // ki üç mount yerinde (mobil profil / masaüstü profil / global host)
  // ayrışamasın.
  useBilgiSayfasiUrl("star", open, onClose);


  return (
    <div
      className={`sri-backdrop${open ? " open" : ""}`}
      ref={setBackdrop}
      onMouseDown={(e) => e.target === backdrop && onClose()}
      role="presentation"
      aria-hidden={!open}
    >
      <div className={`sri-sheet${open ? " open" : ""}`} ref={sheetRef} {...katman} aria-label="5★ nedir?">
        <div className="sri-handle" />
        <div className="sri-hero">
          <span className="sri-badge" aria-hidden="true">★</span>
          <h2 className="sri-title">5★ MyLASA'da ne demek?</h2>
          <p className="sri-lead">
            Yıldızlar MyLASA'da içeriklerin ve katkıların kalitesini anlatan ana sinyaldir.
          </p>
        </div>

        <div className="sri-list">
          {POINTS.map((p) => (
            <div className="sri-item" key={p.title}>
              <span className="sri-item-icon" aria-hidden="true">{p.icon}</span>
              <div className="sri-item-body">
                <div className="sri-item-title">{p.title}</div>
                <div className="sri-item-desc">{p.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <p className="sri-foot">
          Kurallara aykırı davranışlar yıldız sisteminin etkisini azaltabilir.
        </p>

        <button type="button" className="sri-close" onClick={onClose}>
          Anladım
        </button>
        <div className="sri-safe" />
      </div>
    </div>
  );
}

// ─── Global lazy-mount (ToastBoot deseni) ───────────────────────────────────
let container: HTMLDivElement | null = null;
let root: ReturnType<typeof createRoot> | null = null;
let setOpenFn: ((v: boolean) => void) | null = null;

function StarInfoHost() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setOpenFn = setOpen;
    return () => { setOpenFn = null; };
  }, []);
  return <StarInfoSheet open={open} onClose={() => setOpen(false)} />;
}

function ensureHost() {
  if (!container) {
    container = document.createElement("div");
    container.id = "star-info-root";
    document.body.appendChild(container);
    root = createRoot(container);
    root.render(<StarInfoHost />);
  }
}

/** 5★ anlatım sheet'ini aç. Herhangi bir rating yüzeyinden çağrılabilir. */
export function openStarInfo() {
  ensureHost();
  // Host mount → setOpenFn bir microtask sonra hazır olabilir; iki denemede aç.
  if (setOpenFn) setOpenFn(true);
  else setTimeout(() => setOpenFn && setOpenFn(true), 0);
}

export default StarInfoSheet;
